import React, { useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Descriptions,
  Divider,
  Modal,
  notification,
  Space,
  Typography,
  Switch,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  BellOutlined,
  LockOutlined,
  SendOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import TelegramLogin from "../buttons/TelegramLogin";
import settings from "../settings";
import "./UserSettings.css";

const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

const UserSettings = ({ visible, onCancel }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [isLoading, setIsLoading] = useState(false);

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    priceAlerts: true,
    newReleases: false,
    marketUpdates: true,
  });

  const handleToggleNotification = (key, checked) => {
    setNotificationSettings({
      ...notificationSettings,
      [key]: checked,
    });

    notification.success({
      message: "Settings Updated",
      description: "Your notification preferences have been saved.",
    });
  };

  const showDeleteConfirm = () => {
    confirm({
      title: "Are you sure you want to delete your account?",
      icon: <ExclamationCircleOutlined />,
      content:
        "This action cannot be undone. All your data will be permanently deleted.",
      okText: "Yes, delete my account",
      okType: "danger",
      cancelText: "No, cancel",
      onOk() {
        handleDeleteAccount();
      },
    });
  };

  const handleDeleteAccount = async () => {
    try {
      setIsLoading(true);
      const accessToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://valuedraw.com/api/v1",
          scope: "read:current_user",
        },
      });
      const response = await fetch(`${settings.API_BE_BASE_URL}/users/self/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        notification.success({
          message: "Success",
          description: "Your account has been successfully deleted.",
        });
        onCancel();
        // Here you could trigger a logout
      } else {
        const errorData = await response.json();
        notification.error({
          message: "Error",
          description: `Failed to delete account: ${errorData.message || "Unknown error"}`,
        });
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "An error occurred while deleting the account.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      className="user-settings-modal"
      destroyOnClose={true}
    >
      <div className="user-settings-container">
        <div className="user-settings-header">
          <Title level={3}>User Settings</Title>
          <Text type="secondary">
            Manage your account preferences and connections
          </Text>
        </div>

        {user && (
          <>
            <Card className="user-settings-card">
              <div className="user-profile-avatar">
                {user.picture ? (
                  <Avatar size={80} src={user.picture} />
                ) : (
                  <Avatar size={80} icon={<UserOutlined />} />
                )}
              </div>

              <Title
                level={4}
                style={{ textAlign: "center", marginBottom: 24 }}
              >
                {user.name}
              </Title>

              <div className="user-account-details">
                <Descriptions column={1} size="small" bordered={false}>
                  <Descriptions.Item label="Email">
                    {user.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nickname">
                    {user.nickname}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email Verified">
                    {user.email_verified ? (
                      <Text type="success">Yes</Text>
                    ) : (
                      <Text type="warning">No</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="User ID">
                    <Text copyable ellipsis style={{ maxWidth: 200 }}>
                      {user.sub}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </Card>

            <Card
              title={
                <span>
                  <BellOutlined /> Notification Preferences
                </span>
              }
              className="user-settings-card"
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Text strong>Email Notifications</Text>
                    <div>
                      <Text type="secondary">
                        Receive general email updates
                      </Text>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.emailNotifications}
                    onChange={(checked) =>
                      handleToggleNotification("emailNotifications", checked)
                    }
                  />
                </div>

                <Divider style={{ margin: "12px 0" }} />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Text strong>Price Alerts</Text>
                    <div>
                      <Text type="secondary">
                        Get notified when tracked cards reach target prices
                      </Text>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.priceAlerts}
                    onChange={(checked) =>
                      handleToggleNotification("priceAlerts", checked)
                    }
                  />
                </div>

                <Divider style={{ margin: "12px 0" }} />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Text strong>New Releases</Text>
                    <div>
                      <Text type="secondary">
                        Updates about new card set releases
                      </Text>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.newReleases}
                    onChange={(checked) =>
                      handleToggleNotification("newReleases", checked)
                    }
                  />
                </div>

                <Divider style={{ margin: "12px 0" }} />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Text strong>Market Updates</Text>
                    <div>
                      <Text type="secondary">
                        Weekly summaries of market trends
                      </Text>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.marketUpdates}
                    onChange={(checked) =>
                      handleToggleNotification("marketUpdates", checked)
                    }
                  />
                </div>
              </Space>
            </Card>

            <Card
              title={
                <span>
                  <SendOutlined /> Integrations
                </span>
              }
              className="user-settings-card"
            >
              <div className="user-integrations">
                <Paragraph>
                  Connect your account with third-party services:
                </Paragraph>

                <div className="integration-item">
                  <SendOutlined className="integration-item-icon" />
                  <div className="integration-item-content">
                    <div className="integration-item-title">Telegram</div>
                    <div className="integration-item-status">
                      Connect to receive notifications via Telegram
                    </div>
                  </div>
                  <TelegramLogin />
                </div>

                <Tooltip title="More integrations coming soon">
                  <Button
                    icon={<QuestionCircleOutlined />}
                    type="dashed"
                    block
                    style={{ marginTop: 12 }}
                  >
                    More Integrations Coming Soon
                  </Button>
                </Tooltip>
              </div>
            </Card>

            <div className="danger-zone">
              <div className="danger-zone-title">
                <DeleteOutlined style={{ color: "#f5222d", marginRight: 8 }} />
                <span>Danger Zone</span>
              </div>

              <div className="danger-zone-warning">
                <Paragraph>
                  Once you delete your account, there is no going back. Please
                  be certain.
                </Paragraph>
              </div>

              <Button
                type="primary"
                danger
                onClick={showDeleteConfirm}
                loading={isLoading}
                icon={<DeleteOutlined />}
              >
                Delete My Account
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default UserSettings;
